/**
 * API Client for Apex Program Builder
 * 
 * Centralized API communication with credentials support
 */

const API_BASE_URL = import.meta.env.PROD
  ? '/api'
  : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api`;

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchAPI<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const config: RequestInit = {
    ...options,
    credentials: 'include', // Important for session cookies
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    // Try to parse JSON response
    let data: any;
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      throw new ApiError(
        data?.message || data?.error || 'Request failed',
        response.status,
        data
      );
    }

    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or other errors
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}

// ── Plans API ──

export const plansAPI = {
  list: () => fetchAPI<{ plans: any[] }>('/plans'),

  get: (id: string) => fetchAPI<{ plan: any }>(`/plans/${id}`),

  create: (data: any) => fetchAPI<{ plan: any }>('/plans', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id: string, data: any) => fetchAPI<{ plan: any }>(`/plans/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (id: string) => fetchAPI<{ message: string; id: string }>(`/plans/${id}`, {
    method: 'DELETE',
  }),

  duplicate: (id: string) => fetchAPI<{ plan: any }>(`/plans/${id}/duplicate`, {
    method: 'POST',
  }),

  share: (id: string, email: string) => fetchAPI<{ plan: any }>(`/plans/${id}/share`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),
};

// ── Exercises API ──

export const exercisesAPI = {
  list: () => fetchAPI<{ exercises: any[] }>('/exercises'),

  get: (id: string) => fetchAPI<{ exercise: any }>(`/exercises/${id}`),

  create: (data: any) => fetchAPI<{ exercise: any }>('/exercises', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id: string, data: any) => fetchAPI<{ exercise: any }>(`/exercises/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (id: string) => fetchAPI<{ message: string; id: string }>(`/exercises/${id}`, {
    method: 'DELETE',
  }),
};

// ── Surveys API ──

export const surveysAPI = {
  list: () => fetchAPI<{ surveys: any[] }>('/surveys'),

  get: (id: string) => fetchAPI<{ survey: any }>(`/surveys/${id}`),

  create: (data: any) => fetchAPI<{ survey: any }>('/surveys', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id: string, data: any) => fetchAPI<{ survey: any }>(`/surveys/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (id: string) => fetchAPI<{ message: string; id: string }>(`/surveys/${id}`, {
    method: 'DELETE',
  }),

  // Public endpoints (no auth required)
  getPublic: (token: string) => fetchAPI<{ survey: any }>(`/surveys/public/${token}`),

  submitResponse: (token: string, answers: any) => fetchAPI<{ response: any }>(
    `/surveys/public/${token}/responses`,
    {
      method: 'POST',
      body: JSON.stringify({ answers }),
    }
  ),

  getResponses: (surveyId: string) => fetchAPI<{ responses: any[] }>(`/surveys/${surveyId}/responses`),
};

// Export error class for error handling
export { ApiError };

// Default export with all APIs
export default {
  plans: plansAPI,
  exercises: exercisesAPI,
  surveys: surveysAPI,
};
